import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, User } from 'lucide-react';
import { useTheme, type ThemeName } from '@/contexts/ThemeContext';
import { useAthleteAuth } from '@/contexts/AthleteAuthContext';
import { useThemeImages } from '@/hooks/useThemeImages';

const THEME_COLORS: Record<ThemeName, string> = {
  mountain: 'bg-green-700',
  weave: 'bg-red-700',
  river: 'bg-blue-700',
};

export default function Header() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { athlete } = useAthleteAuth();
  const themeImages = useThemeImages();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { to: '/', label: t('nav.home'), exact: true },
    { to: '/announcements', label: t('nav.announcements') },
    { to: '/competitions', label: t('nav.competitions') },
    { to: '/scores', label: t('nav.liveScores') },
    { to: '/schedule', label: t('nav.schedule') },
    { to: '/venues', label: t('nav.venues') },
    { to: '/media', label: t('nav.media') },
    { to: '/stats', label: t('nav.stats') },
  ];

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'zh-TW' ? 'en' : 'zh-TW');
  };

  return (
    <header className="sticky top-0 z-50 bg-header-bg text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Title */}
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src={themeImages.logo} alt="Logo" className="w-10 h-10 rounded-full object-cover bg-white/10" />
            <div className="hidden sm:block">
              <div className="text-sm font-bold leading-tight">第14屆全國泰雅族運動會</div>
              <div className="text-xs text-white/70">14th National Atayal Games</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Right controls */}
          <div className="flex items-center gap-2">
            {/* Theme Switcher */}
            <div className="flex items-center gap-1.5">
              {(Object.keys(THEME_COLORS) as ThemeName[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTheme(t)}
                  className={`w-5 h-5 rounded-full border-2 transition-transform ${THEME_COLORS[t]} ${
                    theme === t ? 'border-white scale-125' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                  aria-label={t}
                />
              ))}
            </div>

            {/* Athlete Login / Portal */}
            {athlete ? (
              <Link
                to="/athlete/dashboard"
                className="hidden sm:flex items-center gap-1.5 ml-2 px-2.5 py-1 text-xs font-bold rounded border border-white/30 hover:bg-white/10 transition-colors"
              >
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold">
                  {athlete.name?.charAt(0) || <User className="w-3 h-3" />}
                </div>
                <span>選手專區</span>
              </Link>
            ) : (
              <Link
                to="/athlete/login"
                className="hidden sm:flex items-center gap-1.5 ml-2 px-2.5 py-1 text-xs font-bold rounded border border-white/30 hover:bg-white/10 transition-colors"
              >
                <User className="w-3.5 h-3.5" />
                <span>選手登入</span>
              </Link>
            )}

            {/* Language Toggle */}
            <button
              onClick={toggleLang}
              className="ml-2 px-2 py-1 text-xs font-bold rounded border border-white/30 hover:bg-white/10 transition-colors"
            >
              {i18n.language === 'zh-TW' ? 'EN' : '中'}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden ml-2 p-1.5 rounded-md hover:bg-white/10"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-white/10">
          <nav className="max-w-7xl mx-auto px-4 py-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'text-white/80 hover:bg-white/10'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {/* Extra nav items for mobile */}
            <NavLink
              to="/transportation"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'bg-white/20 text-white' : 'text-white/80 hover:bg-white/10'
                }`
              }
            >
              {t('nav.transportation')}
            </NavLink>
            <NavLink
              to="/accommodation"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'bg-white/20 text-white' : 'text-white/80 hover:bg-white/10'
                }`
              }
            >
              {t('nav.accommodation')}
            </NavLink>
            <NavLink
              to="/contacts"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'bg-white/20 text-white' : 'text-white/80 hover:bg-white/10'
                }`
              }
            >
              {t('nav.contacts')}
            </NavLink>
            {/* Athlete Login / Portal (mobile) */}
            <div className="border-t border-white/10 mt-2 pt-2">
              {athlete ? (
                <NavLink
                  to="/athlete/dashboard"
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                      isActive ? 'bg-white/20 text-white' : 'text-white/80 hover:bg-white/10'
                    }`
                  }
                >
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold">
                    {athlete.name?.charAt(0) || '?'}
                  </div>
                  選手專區 / My Portal
                </NavLink>
              ) : (
                <NavLink
                  to="/athlete/login"
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                      isActive ? 'bg-white/20 text-white' : 'text-white/80 hover:bg-white/10'
                    }`
                  }
                >
                  <User className="w-4 h-4" />
                  選手登入 / Athlete Login
                </NavLink>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
